// ###### IMPORTS
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ScaleIcon,
  ChevronDownIcon,
  PlusIcon,
  TrashIcon,
  PencilIcon,
} from '@heroicons/react/24/solid';
import { toast } from 'react-hot-toast';
import {
  getConfigCustomUpdateSteps,
  patchConfigCustomUpdateInstructions,
} from '../../viewServices/assets_api';
import { formatApiError } from '../../viewServices/snapshot_form_shared';
import SpinnerLoad from 'views/finance/moduleComponents/SpinnerLoad';
import FormNewSnapshotInline from './FormNewSnapshotInline';
import FormReturnsBatchInline from './FormReturnsBatchInline';
import TemporaryInfoPanel from './TemporaryInfoPanel';
import WorkflowOperationsPanel from './WorkflowOperationsPanel';

const normalizeInstructionStepsFromApi = (list) => {
  if (!Array.isArray(list)) return [];
  return [...list]
    .map((s, i) => ({
      id: String(s.id ?? `step_${i}`),
      label: String(s.label ?? ''),
      order: typeof s.order === 'number' ? s.order : i + 1,
      checked: !!s.checked,
    }))
    .sort((a, b) => a.order - b.order || a.id.localeCompare(b.id));
};

const instructionStepsToPayload = (instructionSteps) =>
  instructionSteps.map((s, i) => ({
    id: s.id.trim(),
    label: s.label.trim(),
    order: i + 1,
    checked: !!s.checked,
  }));

const newInstructionStepId = () =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `step_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

/** Hardcoded workflow accordion below the checklist (no link to persisted instructions). */
const PERSISTENT_ACCORDION_SECTION_IDS = new Set([1, 2]);

const CustomUpdateWorkflowAccordion = () => {
  const [open, setOpen] = useState({ 1: false, 2: false, 3: false, 4: false });
  const [mountedSections, setMountedSections] = useState({});

  const toggle = (id) => {
    setOpen((prev) => {
      const willOpen = !prev[id];
      if (willOpen && PERSISTENT_ACCORDION_SECTION_IDS.has(id)) {
        setMountedSections((current) => (current[id] ? current : { ...current, [id]: true }));
      }
      return { ...prev, [id]: willOpen };
    });
  };

  const renderSectionBody = (id) => {
    if (id === 1) return <TemporaryInfoPanel />;
    if (id === 2) return <WorkflowOperationsPanel />;
    if (id === 3) return <FormNewSnapshotInline />;
    if (id === 4) return <FormReturnsBatchInline />;
    return null;
  };

  const sections = [
    { id: 1, title: '1. Temporary Info' },
    { id: 2, title: '2. Assets and Operations' },
    { id: 3, title: '3. New Snapshot' },
    { id: 4, title: '4. Returns' },
  ];

  return (
    <div className="mt-5 border-t border-gray-200 pt-5">
      <h3 className="mb-2.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
        Workflow details
      </h3>
      <div className="flex flex-col gap-2.5">
        {sections.map(({ id, title }) => {
          const isOpen = open[id];
          const isPersistent = PERSISTENT_ACCORDION_SECTION_IDS.has(id);
          const isMounted = isPersistent ? mountedSections[id] : isOpen;
          return (
            <div key={id} className="overflow-visible">
              <button
                type="button"
                onClick={() => toggle(id)}
                aria-expanded={isOpen}
                aria-controls={`custom-update-acc-panel-${id}`}
                id={`custom-update-acc-head-${id}`}
                className="flex w-full items-center justify-between gap-2 rounded-lg bg-gray-100 px-3 py-1.5 text-left text-sm font-medium text-gray-800 hover:bg-gray-200/70"
              >
                <span>{title}</span>
                <ChevronDownIcon
                  className={`h-5 w-5 shrink-0 text-gray-500 transition-transform ${
                    isOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>
              {isMounted && (
                <div
                  id={`custom-update-acc-panel-${id}`}
                  role="region"
                  aria-labelledby={`custom-update-acc-head-${id}`}
                  hidden={!isOpen}
                  className={isOpen ? 'bg-white py-3.5' : 'hidden'}
                >
                  {renderSectionBody(id)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ##### MAIN COMPONENT
const CardCustomUpdateBalances = () => {
  const [instructionSteps, setInstructionSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  /** Save list / add-remove / labels — not used for checkbox toggles (silent optimistic). */
  const [savingInstructionList, setSavingInstructionList] = useState(false);
  const [dirty, setDirty] = useState(false);
  /** false = read-only checklist UI; true = edit labels / add / remove */
  const [editMode, setEditMode] = useState(false);
  const instructionStepsBaselineRef = useRef([]);
  const instructionStepsRef = useRef([]);

  useEffect(() => {
    instructionStepsRef.current = instructionSteps;
  }, [instructionSteps]);

  const applyServerInstructionSteps = useCallback((normalized) => {
    instructionStepsBaselineRef.current = normalized.map((s) => ({ ...s }));
    setInstructionSteps(normalized);
    setDirty(false);
    if (normalized.length === 0) {
      setEditMode(true);
    }
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const res = await getConfigCustomUpdateSteps();
      const raw = res.data;
      const list = Array.isArray(raw) ? raw : [];
      const normalized = normalizeInstructionStepsFromApi(list);
      applyServerInstructionSteps(normalized);
      setEditMode(normalized.length === 0);
    } catch (e) {
      setFetchError(e?.response?.data?.detail || e?.message || 'Failed to load instructions.');
      setInstructionSteps([]);
      setEditMode(false);
    } finally {
      setLoading(false);
    }
  }, [applyServerInstructionSteps]);

  useEffect(() => {
    reload();
  }, [reload]);

  const persistInstructionStepsInBackground = useCallback(
    async (payload) => {
      const { data } = await patchConfigCustomUpdateInstructions(payload);
      const list = Array.isArray(data) ? data : [];
      const normalized = normalizeInstructionStepsFromApi(list);
      applyServerInstructionSteps(normalized);
      return normalized;
    },
    [applyServerInstructionSteps]
  );

  const persistInstructionList = useCallback(
    async (payload) => {
      setSavingInstructionList(true);
      try {
        return await persistInstructionStepsInBackground(payload);
      } catch (e) {
        toast.error(formatApiError(e.response?.data) || e.message || 'Failed to save.');
        throw e;
      } finally {
        setSavingInstructionList(false);
      }
    },
    [persistInstructionStepsInBackground]
  );

  const handleToggleChecked = useCallback(
    async (id) => {
      const prev = instructionStepsRef.current;
      const previousInstructionSteps = prev.map((s) => ({ ...s }));
      const updatedInstructionSteps = prev.map((s) =>
        s.id === id ? { ...s, checked: !s.checked } : s
      );
      setInstructionSteps(updatedInstructionSteps);
      try {
        await persistInstructionStepsInBackground(
          instructionStepsToPayload(updatedInstructionSteps)
        );
      } catch (e) {
        setInstructionSteps(previousInstructionSteps);
        toast.error(
          formatApiError(e.response?.data) || e.message || 'Failed to update checklist.'
        );
      }
    },
    [persistInstructionStepsInBackground]
  );

  const updateInstructionStepLabel = useCallback((id, label) => {
    setInstructionSteps((prev) => prev.map((s) => (s.id === id ? { ...s, label } : s)));
    setDirty(true);
  }, []);

  const addInstructionStep = useCallback(() => {
    setInstructionSteps((prev) => {
      const maxOrder = prev.reduce((m, s) => Math.max(m, s.order), 0);
      return [
        ...prev,
        {
          id: newInstructionStepId(),
          label: '',
          order: maxOrder + 1,
          checked: false,
        },
      ];
    });
    setDirty(true);
  }, []);

  const removeInstructionStep = useCallback((id) => {
    setInstructionSteps((prev) => prev.filter((s) => s.id !== id));
    setDirty(true);
  }, []);

  const handleSaveInstructionList = useCallback(async () => {
    for (const s of instructionSteps) {
      if (!s.label.trim()) {
        toast.error('Each instruction needs a non-empty label.');
        return;
      }
    }
    try {
      const normalized = await persistInstructionList(
        instructionStepsToPayload(instructionSteps)
      );
      toast.success('Instructions saved.');
      if (normalized.length > 0) {
        setEditMode(false);
      }
    } catch {
      /* toast in persistInstructionList */
    }
  }, [instructionSteps, persistInstructionList]);

  const cancelEdit = useCallback(() => {
    setInstructionSteps(instructionStepsBaselineRef.current.map((s) => ({ ...s })));
    setEditMode(false);
    setDirty(false);
  }, []);

  return (
    <div className="flex h-auto w-full flex-col gap-2 bg-white shadow rounded-xl p-4">
      <div className="-m-4 mb-2 rounded-t-xl border-b border-gray-200 bg-green-800">
        <div className="flex items-center gap-2 px-4 py-2">
          <ScaleIcon className="h-4 w-4 shrink-0 text-white" />
          <h2 className="truncate text-sm font-medium text-white">Custom Update Balances</h2>
        </div>
      </div>

      <div className="relative">
        <button
          type="button"
          onClick={() => setEditMode(true)}
          disabled={loading || !!fetchError || editMode}
          className="absolute right-0 top-0 z-10 rounded-md p-1 text-gray-400 hover:text-gray-600 disabled:pointer-events-none disabled:opacity-40"
          title="Edit instructions list"
          aria-label="Edit instructions list"
        >
          <PencilIcon className="h-5 w-5" />
        </button>

        <p className="mb-2 pr-10 text-sm leading-snug text-gray-600">
          Follow the checklist below for the custom balance update process.
        </p>

        {fetchError && (
          <p className="text-sm text-red-600" role="alert">
            {fetchError}
          </p>
        )}

        {loading ? (
          <div className="flex justify-center py-4">
            <SpinnerLoad />
          </div>
        ) : instructionSteps.length === 0 && !fetchError ? (
          <div className="rounded-lg border border-gray-200 bg-gray-50/80 px-3 py-3 text-center">
            <p className="text-sm leading-snug text-gray-600">No instructions yet.</p>
            {editMode ? (
              <button
                type="button"
                onClick={addInstructionStep}
                disabled={savingInstructionList}
                className="mt-2 inline-flex items-center gap-1 rounded-lg border border-green-200 bg-green-50 px-2.5 py-1.5 text-xs font-medium text-green-800 hover:bg-green-100 disabled:opacity-50"
              >
                <PlusIcon className="h-4 w-4" />
                Add instruction
              </button>
            ) : (
              <p className="mt-2 text-xs leading-snug text-gray-500">Use the edit button to add instructions.</p>
            )}
          </div>
        ) : !fetchError ? (
          <div className="flex flex-col gap-2">
          <div className="rounded-lg border border-gray-200 bg-gray-50/90 px-3 py-2">
            <ul className="m-0 flex list-none flex-col gap-2 p-0">
              {instructionSteps.map((step) => (
                <li key={step.id} className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-2">
                  <div className="flex shrink-0 items-center gap-2">
                    <input
                      type="checkbox"
                      id={`custom-update-step-${step.id}`}
                      checked={!!step.checked}
                      onChange={() => handleToggleChecked(step.id)}
                      className="shrink-0 rounded border-gray-300 text-green-700 focus:ring-green-600"
                    />
                    <label
                      htmlFor={`custom-update-step-${step.id}`}
                      className="sr-only"
                    >
                      Done: {step.label || 'instruction'}
                    </label>
                  </div>
                  {editMode ? (
                    <>
                      <input
                        type="text"
                        value={step.label}
                        onChange={(e) => updateInstructionStepLabel(step.id, e.target.value)}
                        disabled={savingInstructionList}
                        className="min-w-0 flex-1 rounded border border-gray-200 bg-white px-2 py-1 text-sm leading-snug text-gray-800"
                        placeholder="New item"
                        aria-label="Instruction text"
                      />
                      <button
                        type="button"
                        onClick={() => removeInstructionStep(step.id)}
                        disabled={savingInstructionList}
                        className="shrink-0 rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
                        title="Remove instruction"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </>
                  ) : (
                    <span className="min-w-0 flex-1 text-sm leading-snug text-gray-800">
                      {step.label}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
          {editMode ? (
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={addInstructionStep}
                disabled={savingInstructionList}
                className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                <PlusIcon className="h-4 w-4" />
                Add instruction
              </button>
              <button
                type="button"
                onClick={handleSaveInstructionList}
                disabled={savingInstructionList || !dirty}
                className="rounded-lg bg-green-700 px-2.5 py-1 text-xs font-medium text-white hover:bg-green-800 disabled:cursor-not-allowed disabled:bg-green-300"
              >
                {savingInstructionList ? 'Saving…' : 'Save instructions'}
              </button>
              <button
                type="button"
                onClick={cancelEdit}
                disabled={savingInstructionList}
                className="rounded-lg border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          ) : null}
        </div>
        ) : null}
      </div>

      <CustomUpdateWorkflowAccordion />
    </div>
  );
};

export default CardCustomUpdateBalances;
